import * as React from "react";
import { useIcon } from "@salt-ds/core";

import { Text } from "./Text";
import "./link.css";

/**
 * Link, replacing `@salt-ds/core`'s behind `link.tsx` and `metric.tsx`.
 *
 * ── A LINK THAT OPENS A NEW TAB SAYS SO, IN WORDS ─────────────────────────────────────
 *
 * `target="_blank"` adds two things: an external-link icon for sighted readers, and a
 * visually-hidden "Opens in a new tab" for everyone else. The icon alone is not enough — a
 * screen-reader user gets no warning that their context is about to change, which is
 * disorienting precisely because nothing announces it.
 *
 * The icon is `aria-hidden` because the hidden text already carries that meaning; announcing
 * both says it twice.
 *
 * ── THE COLOUR PROP FEEDS Text, BUT NOT DIRECTLY ──────────────────────────────────────
 *
 * The link's own colour is applied through a `saltLink-{color}` class, and `Text` is told
 * `color="inherit"` so it does not paint over it. Passing the colour straight to `Text` would
 * work for the base state and lose the hover and visited colours, which live on the link class.
 */
export type LinkColor = "inherit" | "primary" | "secondary" | "accent";
export type LinkUnderline = "default" | "never";

export interface LinkProps extends Omit<React.ComponentPropsWithoutRef<"a">, "color"> {
  IconComponent?: React.ComponentType<Record<string, unknown>> | null;
  color?: LinkColor;
  /** The vendor's deprecated alias for `color`; it wins when both are given, as it did there. */
  variant?: LinkColor;
  underline?: LinkUnderline;
  maxRows?: number;
  styleAs?: "h1" | "h2" | "h3" | "h4" | "label" | "notation" | "code";
  /**
   * Render a DIFFERENT element with this Link's props and styling — a React Router `<Link>`,
   * typically. `link.tsx` uses it so an in-app route gets the design system's link treatment
   * without an `<a>` that would trigger a full page load.
   */
  render?: React.ReactElement;
}

function capitalize(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    IconComponent,
    children,
    className,
    color: colorProp,
    href,
    render,
    target = "_self",
    underline = "default",
    variant,
    ...rest
  },
  ref,
) {
  const { ExternalIcon } = useIcon();
  const color = variant ?? colorProp ?? "primary";
  // `null` is a deliberate "no icon"; `undefined` means "use the default".
  const LinkIcon = IconComponent === undefined ? ExternalIcon : IconComponent;

  const classes = ["saltLink", `saltLink-underline${capitalize(underline)}`];
  if (color !== "inherit") classes.push(`saltLink-${color}`);
  if (className) classes.push(className);

  /*
   * The caller's own props win over ours when rendering into their element, matching the
   * vendor: they chose that element deliberately, and silently overriding its `to` or `onClick`
   * would break the navigation it exists for.
   */
  const content = (
    <>
      {children}
      {target === "_blank" ? (
        <>
          {LinkIcon ? <LinkIcon aria-hidden className="saltLink-icon" /> : null}
          <span className="saltLink-externalLinkADA">Opens in a new tab</span>
        </>
      ) : null}
    </>
  );

  if (React.isValidElement(render)) {
    return (
      <Text
        as={render.type as React.ElementType}
        className={classes.join(" ")}
        color="inherit"
        ref={ref}
        {...rest}
        {...(render.props as Record<string, unknown>)}
      >
        {content}
      </Text>
    );
  }

  return (
    <Text
      as="a"
      className={classes.join(" ")}
      // `inherit` so Text does not paint over the link class, which owns hover and visited too.
      color="inherit"
      href={href}
      ref={ref}
      target={target}
      {...rest}
    >
      {content}
    </Text>
  );
});
